import { DEFAULT_CURRENCY, moneyFormat } from "@redotech/money/util";
import { t } from "@redotech/redo-i18n";
import { PriceOption } from "@redotech/redo-return-core-common/condition-options";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { CONDITION_NAMES } from "@redotech/redo-return-core-common/return-flow/condition-names";
import {
  priceConditionFromModel,
  validatePriceCondition,
  type NonReturnedValueState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";

export const NON_RETURNED_VALUE = (
  Details: React.ComponentType<
    ConditionTypeDetailsProps<NonReturnedValueState>
  >,
): ConditionType<NonReturnedValueState, Condition.NonReturnedValue> => ({
  name: CONDITION_NAMES[Condition.NON_RETURNED_VALUE],
  description(state) {
    if (!state.priceOption) {
      return t(
        "The total return value of all items in the order that the customer is not returning",
      );
    }
    switch (state.priceOption) {
      case PriceOption.LESS_THAN:
        return t(
          "The total return value of all items not returned is less than {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.GREATER_THAN:
        return t(
          "The total return value of all items not returned is greater than {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.EQUALS:
        return t(
          "The total return value of all items not returned equals {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
      case PriceOption.CENTS_EQUALS:
        return t(
          "The total return value of all items not returned has cents equal to {{value}}",
          {
            value: moneyFormat({
              amount: +state.amount,
              currency: DEFAULT_CURRENCY,
            }),
          },
        );
    }
  },
  Details(props: ConditionTypeDetailsProps<NonReturnedValueState>) {
    return React.createElement(Details, props);
  },
  empty: { amount: (0).toFixed(2), priceOption: null },
  fromModel(model) {
    return priceConditionFromModel(model);
  },
  toModel(state) {
    return {
      type: Condition.NON_RETURNED_VALUE,
      amount: +state.amount,
      priceOption: state.priceOption,
    };
  },
  valid(state) {
    return validatePriceCondition(state).valid;
  },
});
